'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'

import { useLocale } from '@/components/LocaleContext'
import { ZoomableImage } from './ZoomableImage'

const MAX_CARD_WIDTH = 700
const MAX_CARD_HEIGHT = 527
const CARD_GAP = 24
const MIN_CARD_WIDTH = 260
const MIN_CARD_WIDTH_NARROW = 220 /* very narrow phones (e.g. Z Fold 374px) */
const MOBILE_BREAKPOINT = 480
const MOBILE_CARD_WIDTH = 232
const MOBILE_CARD_HEIGHT = 174.69

export type ProductCarouselItem = {
  imageSrc: string
  title: string
  href?: string
  partNumber: string
}

export type ProductCarouselProps = {
  products: ProductCarouselItem[]
  loading?: boolean
  className?: string
}

const SKELETON_CARD_COUNT = 6
const EMPTY_PRODUCTS_LABEL: Record<string, string> = {
  en: 'No products to show.',
  fr: 'Aucun produit à afficher.',
  ar: 'لا توجد منتجات للعرض.',
}

/**
 * Infinite horizontal carousel: products as cards, scroll left/right forever.
 * Renders three copies of the list; starts in the middle and recenters when
 * you scroll into the left or right copy so the loop is seamless.
 * Supports loading state so async data does not cause layout shift or broken scroll.
 */
export function ProductCarousel({
  products: productsProp,
  loading = false,
  className = '',
}: ProductCarouselProps) {
  const { locale } = useLocale()
  const products = productsProp
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const jumpingRef = useRef(false)
  const [hasInitialScroll, setHasInitialScroll] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  // New refs for scroll state tracking
  const scrollStateRef = useRef({ cardIndex: 0, offset: 0 })
  const rafRef = useRef<number | undefined>(undefined)
  const activeDotRef = useRef<HTMLButtonElement>(null)
  const dotsContainerRef = useRef<HTMLDivElement>(null)

  const n = products.length
  const list = n === 0 ? [] : [...products, ...products, ...products]
  const isMobile = containerWidth > 0 && containerWidth <= MOBILE_BREAKPOINT
  const minWidth =
    containerWidth > 0 && containerWidth < 420 ? MIN_CARD_WIDTH_NARROW : MIN_CARD_WIDTH
  const cardWidth = isMobile
    ? MOBILE_CARD_WIDTH
    : containerWidth > 0
      ? Math.max(minWidth, Math.min(MAX_CARD_WIDTH, containerWidth - CARD_GAP))
      : MAX_CARD_WIDTH
  const cardHeight = isMobile
    ? MOBILE_CARD_HEIGHT
    : Math.round(cardWidth * (MAX_CARD_HEIGHT / MAX_CARD_WIDTH))
  const cardStep = cardWidth + CARD_GAP
  const setWidth = n * cardStep
  const spacer = Math.max(0, containerWidth - cardWidth)
  const isReady = !loading && n > 0

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth))
    ro.observe(el)
    setContainerWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const recenter = useCallback(() => {
    const el = scrollRef.current
    if (!el || jumpingRef.current || n === 0) return

    const x = el.scrollLeft
    const cardIndex = Math.floor(x / cardStep)

    // Track current position for state preservation
    scrollStateRef.current = {
      cardIndex: cardIndex % n,
      offset: x % cardStep,
    }

    // Calculate which "set" we're in (0 = first copy, 1 = middle copy, 2 = third copy)
    const currentSet = Math.floor(x / setWidth)

    // Only recenter if we're in the first or third copy (not the middle)
    // Jump to middle copy at same visual position so the loop is seamless
    if (currentSet === 0 || currentSet === 2) {
      const offsetInSet = ((cardIndex % n) + n) % n
      const targetScroll = setWidth + offsetInSet * cardStep + (x % cardStep)

      jumpingRef.current = true
      // Force instant scroll so the loop is invisible (scroll-smooth would animate the jump)
      const prevBehavior = el.style.scrollBehavior
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = targetScroll
      el.style.scrollBehavior = prevBehavior

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          jumpingRef.current = false
        })
      })
    }
  }, [n, setWidth, cardStep])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    if (!isReady) {
      // Store current position before loading
      if (hasInitialScroll && n > 0) {
        const x = el.scrollLeft
        scrollStateRef.current = {
          cardIndex: Math.floor(x / cardStep) % n,
          offset: x % cardStep,
        }
      }
      setHasInitialScroll(false)
      return
    }

    if (n === 0) return

    // Restore position after loading
    if (!hasInitialScroll) {
      const { cardIndex, offset } = scrollStateRef.current
      const targetIndex = Math.min(cardIndex, n - 1)
      el.scrollLeft = setWidth + targetIndex * cardStep + offset
      setActiveIndex(targetIndex)
      setHasInitialScroll(true)
    }
  }, [isReady, n, setWidth, cardStep, hasInitialScroll])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || n === 0 || !hasInitialScroll) return

    let isScrolling = false

    const monitorScroll = () => {
      if (isScrolling && !jumpingRef.current) {
        recenter()
        setActiveIndex(scrollStateRef.current.cardIndex)
        rafRef.current = requestAnimationFrame(monitorScroll)
      }
    }

    const handleScroll = () => {
      if (jumpingRef.current) return

      if (!isScrolling) {
        isScrolling = true
        rafRef.current = requestAnimationFrame(monitorScroll)
      }
    }

    const handleScrollEnd = () => {
      isScrolling = false
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      recenter()
      setActiveIndex(scrollStateRef.current.cardIndex)
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    if ('onscrollend' in el) {
      el.addEventListener('scrollend', handleScrollEnd as EventListener)
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.removeEventListener('scroll', handleScroll)
      if ('onscrollend' in el) {
        el.removeEventListener('scrollend', handleScrollEnd as EventListener)
      }
    }
  }, [n, setWidth, recenter, hasInitialScroll])

  const snap = useCallback(() => {
    const el = scrollRef.current
    if (!el || jumpingRef.current || list.length === 0) return
    const x = el.scrollLeft
    const i = Math.round(x / cardStep)
    const target = i * cardStep
    // Only snap if we're slightly off, don't clamp to list bounds
    if (Math.abs(x - target) > 2) {
      el.scrollLeft = target
    }
  }, [cardStep, list.length])

  const scrollToIndex = useCallback(
    (index: number, instant = false) => {
      const el = scrollRef.current
      if (!el || index < 0 || index >= n) return
      const left = setWidth + index * cardStep
      el.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' })
    },
    [n, setWidth, cardStep],
  )

  // One dot per product – count updates dynamically with data
  const PAGINATION_DOTS = n
  const activeDotIndex = Math.min(n - 1, Math.max(0, activeIndex))
  const scrollToDot = useCallback(
    (dotIndex: number) => {
      scrollToIndex(Math.min(n - 1, Math.max(0, dotIndex)), true)
    },
    [n, scrollToIndex],
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el || !hasInitialScroll) return
    let t: ReturnType<typeof setTimeout>
    const onStop = () => {
      clearTimeout(t)
      t = setTimeout(() => {
        snap()
      }, 100)
    }
    const onScrollEnd = () => {
      snap()
    }
    el.addEventListener('scroll', onStop, { passive: true })
    if ('onscrollend' in el) el.addEventListener('scrollend', onScrollEnd as EventListener)
    return () => {
      clearTimeout(t)
      el.removeEventListener('scroll', onStop)
      if ('onscrollend' in el) el.removeEventListener('scrollend', onScrollEnd as EventListener)
    }
  }, [snap, hasInitialScroll])

  // Scroll the dot strip horizontally to bring the active dot into view.
  // IMPORTANT: Do NOT use scrollIntoView() — it scrolls the document when the
  // ProductsSection is below the fold, causing the page to jump down on load.
  // We only scroll the dot strip container (overflow-x-auto), never the document.
  useEffect(() => {
    const dot = activeDotRef.current
    const container = dotsContainerRef.current
    if (!dot || !container) return

    const containerRect = container.getBoundingClientRect()
    const dotRect = dot.getBoundingClientRect()
    const dotCenter = dotRect.left - containerRect.left + dotRect.width / 2
    const containerCenter = container.clientWidth / 2
    const scrollLeft = container.scrollLeft + dotCenter - containerCenter

    const prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    })
  }, [activeDotIndex])

  if (loading) {
    return (
      <div
        ref={containerRef}
        className={`relative w-full ${className}`.trim()}
        role="region"
        aria-label="Product carousel"
        aria-busy="true"
      >
        <div className="overflow-x-hidden overflow-y-hidden py-4">
          <div className="flex animate-pulse" style={{ gap: CARD_GAP }}>
            {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 bg-white shadow-lg overflow-hidden"
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  minWidth: cardWidth,
                }}
              >
                <div className="w-full h-full flex flex-col p-6">
                  <div className="flex-1 min-h-0 flex items-center justify-center rounded-lg bg-[#E8E8E8]" />
                  <div className="h-6 bg-[#E8E8E8] rounded mt-2 w-2/3 shrink-0" />
                  <div className="mt-auto pt-4 shrink-0 flex justify-end">
                    <div className="w-14 h-14 rounded-full bg-[#E8E8E8]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (n === 0) {
    const emptyLabel = EMPTY_PRODUCTS_LABEL[locale] ?? EMPTY_PRODUCTS_LABEL.en
    return (
      <div
        ref={containerRef}
        className={`relative w-full ${className}`.trim()}
        role="region"
        aria-label="Product carousel"
      >
        <div className="py-4 text-center text-[#666]">{emptyLabel}</div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`.trim()}
      role="region"
      aria-label="Product carousel"
    >
      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-mandatory touch-manipulation"
        style={{ WebkitOverflowScrolling: 'touch' }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            scrollRef.current?.scrollBy({ left: -cardStep, behavior: 'smooth' })
          } else if (e.key === 'ArrowRight') {
            e.preventDefault()
            scrollRef.current?.scrollBy({ left: cardStep, behavior: 'smooth' })
          }
        }}
      >
        <div className="flex pb-4 pt-4" style={{ gap: CARD_GAP }}>
          {list.map((product, i) => (
            <article
              key={i}
              className="product-carousel-card flex-shrink-0 snap-start snap-always bg-white shadow-lg overflow-hidden transition-shadow hover:shadow-xl"
              style={{
                width: cardWidth,
                height: cardHeight,
                minWidth: cardWidth,
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
              }}
            >
              <div className="w-full h-full relative flex flex-col p-0">
                <div className="relative w-full h-[80%] flex items-center justify-center">
                  <div className="product-carousel-image-container relative h-full max-h-full aspect-square max-w-[85%] w-auto">
                    <ZoomableImage
                      src={product.imageSrc}
                      alt={product.title}
                      fill
                      className="product-carousel-image object-contain transition-transform duration-300 ease-in-out hover:scale-105"
                      sizes="(max-width: 700px) 100vw, 700px"
                    />
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3 max-[400px]:bottom-2 max-[400px]:left-2 max-[400px]:right-2 md:bottom-6 md:left-6 md:right-6 flex items-center justify-between gap-2 md:gap-3 min-w-0">
                  <p
                    className="font-bebas-neue font-medium text-black text-2xl max-[400px]:text-xl sm:text-4xl md:text-5xl lg:text-[61.6px] leading-tight lg:leading-[68px] tracking-normal capitalize min-w-0 truncate transition-transform duration-300 ease-in-out hover:scale-[1.02]"
                    style={{ letterSpacing: 0 }}
                  >
                    {product.partNumber}
                  </p>
                  <a
                    href={product.href ?? '#'}
                    aria-label={`View ${product.title?.toLowerCase()}`}
                    className="product-carousel-arrow-btn flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] max-[400px]:w-10 max-[400px]:h-10 max-[480px]:w-[19.89px] max-[480px]:h-[19.89px] max-[480px]:min-w-[19.89px] max-[480px]:min-h-[19.89px] max-[480px]:rounded-full md:w-14 md:h-14 shrink-0 rounded-full bg-[#E60023] text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 shadow-md transition-transform duration-300 ease-in-out hover:scale-105 active:scale-95"
                  >
                    {locale === 'ar' ? (
                      <LuArrowUpLeft
                        size={24}
                        aria-hidden
                        className="product-carousel-arrow-icon w-5 h-5 max-[480px]:!w-[10px] max-[480px]:!h-[10px] md:w-6 md:h-6 shrink-0"
                      />
                    ) : (
                      <LuArrowUpRight
                        size={24}
                        aria-hidden
                        className="product-carousel-arrow-icon w-5 h-5 max-[480px]:!w-[10px] max-[480px]:!h-[10px] md:w-6 md:h-6 shrink-0"
                      />
                    )}
                  </a>
                </div>
              </div>
            </article>
          ))}
          {spacer > 0 && (
            <div
              className="flex-shrink-0"
              style={{ width: spacer, minWidth: spacer }}
              aria-hidden
            />
          )}
        </div>
      </div>
      {/* Flat, minimal scroll indicator – thin bars, 44px touch target */}
      <div
        ref={dotsContainerRef}
        className="flex items-center justify-start md:justify-center gap-1.5 pt-5 pb-3 max-w-full overflow-x-auto overflow-y-hidden scroll-smooth"
        style={{ WebkitOverflowScrolling: 'touch' }}
        role="tablist"
        aria-label="Carousel position"
      >
        {Array.from({ length: PAGINATION_DOTS }).map((_, index) => (
          <button
            key={index}
            ref={activeDotIndex === index ? activeDotRef : undefined}
            type="button"
            role="tab"
            aria-selected={activeDotIndex === index}
            aria-label={`Go to section ${index + 1} of ${PAGINATION_DOTS}`}
            onClick={(e) => {
              e.preventDefault()
              scrollToDot(index)
            }}
            className="flex min-h-[44px] min-w-[44px] shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E60023] focus-visible:ring-offset-2"
          >
            <span
              className={`block h-1 shrink-0 rounded-full border-0 transition-all duration-200 ${
                activeDotIndex === index
                  ? 'w-6 bg-[#E60023]'
                  : 'w-1.5 bg-[#E60023]/50 hover:bg-[#E60023]/70'
              }`}
              aria-hidden
            />
          </button>
        ))}
      </div>
    </div>
  )
}
